import { MobileHeader } from './MobileHeader';
import { cva, type VariantProps } from 'class-variance-authority';
import { DesktopHeader } from './DesktopHeader';
import { DesktopHeader as DesktopHeaderOld } from './desktop-old/DesktopHeaderOld';
import { DisplayMode } from '../../../types/enum/display-mode';
import type { SearchSuggestionResultGroup } from '../../search/view-models/search-suggestion';
import type { HeaderSearchTrackingConfig } from './HeaderSearch';
import type { CurrentCategoryType } from '../states/current-category-atom';

export const _headerVariants = cva(
    'duration-50 z-fixed w-full transition-all',
    {
        variants: {
            color: {
                default: 'bg-background text-primary', // default with dark text
                transparent: 'bg-transparent text-gray-50', // transparent with light text
                legacy: 'bg-[#FAF9F7]', // legacy background color
                none: 'bg-transparent text-primary', // transparent with dark text
                app: 'bg-white text-primary md:bg-[#F6F6F6]', // member app background color
            },
            mode: {
                default: 'relative',
                fixed: 'fixed md:absolute',
                float: 'absolute',
            },
        },
        defaultVariants: {
            color: 'default',
            mode: 'default',
        },
    }
);

export interface HeaderCartItem {
    id: number;
    title: string;
    price: string;
    quantity: number;
    thumbnail: {
        desktop: string;
        mobile: string;
    };
    isSample: boolean;
}

export interface HeaderCartInfo {
    items: HeaderCartItem[];
    itemTotal: { ShortFormat: string; Amount: number };
    cartCount: number;
}

export interface HeaderBaseProps extends VariantProps<typeof _headerVariants> {
    userData: HeaderUserData;
    cartInfo: HeaderCartInfo;
    currentUrl: string;
    onLogout: () => void;
    onLoginClick: () => void;
    onUserCartClick: () => void;
    warningMessages?: string[];
    onNavigate?: (url: string) => void;
    isLegacyBrokenRemMode?: boolean;
}

export interface HeaderProps extends HeaderBaseProps {
    // Specific props for the main Header component
    currentCountryCode: string;
    countries: HeaderCountryElement[];
    primaryDesktopLinks: HeaderNavLinks;
    primaryMobileLinks: HeaderNavLinks;
    secondaryMobileLinks: HeaderNavLinks;
    profileMobileLinks: HeaderNavLinks;
    displayMode: DisplayMode;
    additionalUserMenuItems?: HeaderNavLinks;
    isSearching?: boolean;
    onMobileSearchClick?: () => void;
    uiUpdatesJune2025Enabled?: boolean;
    fetchSuggestions?: (query: string) => void;
    suggestionData?: SearchSuggestionResultGroup | null;
    headerSearchTrackingConfig?: HeaderSearchTrackingConfig;
}

export interface HeaderCountryElement {
    code: string;
    name: string;
    url: string;
}

export interface HeaderCompanyInbox {
    id: number;
    count: number;
    inboxType?: string;
}

export interface HeaderUserData {
    isAuthorized: boolean;
    avatarSrc?: string;
    firstName: string;
    lastName?: string;
    notificationsCount: number;
    professionals: HeaderUserDataProfessional[];
    inboxCount: number;
    designBoardCount: number;
    orderNotificationCount: number;
    websiteNotificationCount: number;
}

interface HeaderUserDataProfessional {
    id: number;
    title: string;
    logo: string;
    urlSegment: string;
    unreads: number;
}

export interface HeaderNavLinks {
    title?: string;
    links: Array<{
        title: string;
        to: string;
        badgeCount?: number;
        onClick?: () => void;
        external?: boolean;
        isNonRemixRoute?: boolean;
        isSeparator?: boolean;
        prefix?: string;
        category?: CurrentCategoryType;
    }>;
}

export const Header = (props: HeaderProps) => {
    const {
        // Base props
        color,
        userData,
        currentUrl,
        onLogout,
        onLoginClick,
        onUserCartClick,
        warningMessages,
        isLegacyBrokenRemMode,

        // Specific props
        mode,
        currentCountryCode,
        countries,
        primaryDesktopLinks,
        primaryMobileLinks,
        secondaryMobileLinks,
        profileMobileLinks,
        displayMode,
        additionalUserMenuItems,
        isSearching,
        onMobileSearchClick,
        cartInfo,
        uiUpdatesJune2025Enabled,
        onNavigate,
        fetchSuggestions,
        suggestionData,
        headerSearchTrackingConfig,
    } = props;

    // Choose which desktop header to use based on the feature flag
    const DesktopHeaderComponent = uiUpdatesJune2025Enabled
        ? DesktopHeader
        : DesktopHeaderOld;

    return displayMode === DisplayMode.Desktop ? (
        <DesktopHeaderComponent
            // Base props
            color={color}
            userData={userData}
            currentUrl={currentUrl}
            onLogout={onLogout}
            onLoginClick={onLoginClick}
            onUserCartClick={onUserCartClick}
            warningMessages={warningMessages}
            isLegacyBrokenRemMode={isLegacyBrokenRemMode}
            // Specific props
            primaryLinks={primaryDesktopLinks}
            currentCountryCode={currentCountryCode}
            countries={countries}
            additionalUserMenuItems={additionalUserMenuItems}
            cartInfo={cartInfo}
            isSearching={isSearching}
            uiUpdatesJune2025Enabled={uiUpdatesJune2025Enabled}
            onNavigate={onNavigate}
            fetchSuggestions={fetchSuggestions}
            suggestionData={suggestionData}
            headerSearchTrackingConfig={headerSearchTrackingConfig}
        />
    ) : (
        <MobileHeader
            // Base props
            color={color}
            userData={userData}
            currentUrl={currentUrl}
            onLogout={onLogout}
            onLoginClick={onLoginClick}
            onUserCartClick={onUserCartClick}
            warningMessages={warningMessages}
            isLegacyBrokenRemMode={isLegacyBrokenRemMode}
            // Specific props
            mode={mode}
            primaryLinks={primaryMobileLinks}
            secondaryLinks={secondaryMobileLinks}
            profileLinks={profileMobileLinks}
            onMobileSearchClick={onMobileSearchClick}
            cartInfo={cartInfo}
        />
    );
};
